import { useCallback, useMemo } from 'react';
import { useMutation } from '@tanstack/react-query';

import { useOrganizationScopeOrNull } from 'components/OrganizationScope/context';
import { useAuth } from 'contexts/AuthContext';
import {
  cancelLineDispatch,
  getLineDispatchBySource,
  getRecentLineDispatches,
  retryLineDispatch,
  type TLineDispatchAggregateStatus,
  type TLineDispatchSourceType,
  type TLineDispatchStatus,
} from 'api/messages';
import { sendAllMessageStrings } from 'constants/strings';
import {
  DISPATCH_ACKNOWLEDGED_KEY_PREFIX,
  dispatchAcknowledgedStorageKey,
} from 'utils/organizationStorage';

export type LineDispatchRecoveryDecision =
  | 'RUN_PRODUCER'
  | 'RECOVERED'
  | 'TERMINAL_FAILED';

export type RecoverLineDispatchInput = {
  sourceType: TLineDispatchSourceType;
  sourceKey: string;
  sourceRevision: number;
  intentHash: string;
};

type AcknowledgedDispatch = { id: string; acknowledgedAt: number };

/** 操作開始時に決めた組織と acknowledged の保存先。await の後も同じ値を使う。 */
export type LineDispatchScope = {
  organizationId?: number | null;
  acknowledgedStorageKey: string;
};

const UNSCOPED: LineDispatchScope = {
  acknowledgedStorageKey: DISPATCH_ACKNOWLEDGED_KEY_PREFIX,
};

const ACKNOWLEDGED_RETENTION_MS = 24 * 60 * 60 * 1000;
/**
 * clientKeyを失った直後のresponse loss回復として、完了済みoperationを
 * 「送信済み」と見なせる時間。
 * intentHashは本文と送信条件だけのhashで時刻も操作主体も含まないため、
 * 窓を広く取ると「同じ内容をあとでもう一度送る」正当な再送まで送信済みへ
 * 吸収し、producer未実行のまま成功表示になる。
 */
const RESPONSE_LOSS_RECOVERY_WINDOW_MS = 15 * 60 * 1000;

const SUCCESS_STATUSES = new Set<TLineDispatchAggregateStatus>([
  'COMPLETED',
  'PARTIALLY_FAILED',
]);
const FAILED_STATUSES = new Set<TLineDispatchAggregateStatus>([
  'FAILED',
  'CANCELLED',
]);

function getStorage(): Storage | null {
  try {
    return typeof window === 'undefined' ? null : window.localStorage;
  } catch {
    return null;
  }
}

function readAcknowledgedDispatches(storageKey: string): AcknowledgedDispatch[] {
  const raw = getStorage()?.getItem(storageKey);
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    const threshold = Date.now() - ACKNOWLEDGED_RETENTION_MS;
    return parsed.filter(
      (entry): entry is AcknowledgedDispatch =>
        typeof entry === 'object' &&
        entry !== null &&
        typeof (entry as AcknowledgedDispatch).id === 'string' &&
        typeof (entry as AcknowledgedDispatch).acknowledgedAt === 'number' &&
        (entry as AcknowledgedDispatch).acknowledgedAt >= threshold,
    );
  } catch {
    return [];
  }
}

function acknowledgeDispatchId(dispatchId: string, storageKey: string): void {
  const storage = getStorage();
  if (!storage) return;
  const retained = readAcknowledgedDispatches(storageKey).filter(
    (entry) => entry.id !== dispatchId,
  );
  retained.push({ id: dispatchId, acknowledgedAt: Date.now() });
  storage.setItem(storageKey, JSON.stringify(retained.slice(-100)));
}

function isAcknowledgedDispatch(dispatchId: string, storageKey: string): boolean {
  return readAcknowledgedDispatches(storageKey).some(
    (entry) => entry.id === dispatchId,
  );
}

function currentStatus(
  status: TLineDispatchStatus,
): TLineDispatchAggregateStatus {
  return status.summary.status;
}

function terminalDecision(
  status: TLineDispatchStatus,
  storageKey: string,
): LineDispatchRecoveryDecision | null {
  const aggregateStatus = currentStatus(status);
  if (SUCCESS_STATUSES.has(aggregateStatus)) {
    acknowledgeDispatchId(status.dispatch.id, storageKey);
    return 'RECOVERED';
  }
  if (FAILED_STATUSES.has(aggregateStatus)) {
    acknowledgeDispatchId(status.dispatch.id, storageKey);
    return 'TERMINAL_FAILED';
  }
  if (
    aggregateStatus === 'UNKNOWN' &&
    status.dispatch.completedAt !== null
  ) {
    acknowledgeDispatchId(status.dispatch.id, storageKey);
    return 'TERMINAL_FAILED';
  }
  return null;
}

/** terminalDecisionが確定判断を返す状態か（回収するかは別に決める）。 */
function isTerminalDispatch(status: TLineDispatchStatus): boolean {
  const aggregateStatus = currentStatus(status);
  return (
    SUCCESS_STATUSES.has(aggregateStatus) ||
    FAILED_STATUSES.has(aggregateStatus) ||
    (aggregateStatus === 'UNKNOWN' && status.dispatch.completedAt !== null)
  );
}

/**
 * 完了済みoperationがresponse loss回復の窓内かを判定する。
 * @returns 窓内、または判断材料が無い場合true（回収側＝二重送信を避ける方へ倒す）
 */
function isWithinResponseLossWindow(status: TLineDispatchStatus): boolean {
  const reference = status.dispatch.completedAt ?? status.dispatch.startedAt;
  if (!reference) return true;
  const elapsed = Date.now() - new Date(reference).getTime();
  if (Number.isNaN(elapsed)) return true;
  return elapsed <= RESPONSE_LOSS_RECOVERY_WINDOW_MS;
}

/**
 * seal前かつprovider未試行のdispatchは1件も送信していないため、
 * 破棄しても二重送信にならない。滞留したまま新規送信を止め続けないよう自動でcancelする。
 */
function isUnstartedDispatch(status: TLineDispatchStatus): boolean {
  return (
    currentStatus(status) === 'PLANNED' &&
    status.dispatch.sealedAt === null &&
    status.dispatch.startedAt === null &&
    status.dispatch.firstAttemptedAt === null &&
    status.dispatch.cancelledAt === null
  );
}

/**
 * 未着手dispatchを破棄する。backendがcancelを拒んだ場合は開始済みなので、
 * 破棄扱いにせず呼び出し元のretry回収へ委ねる。
 * @returns 破棄できた場合true
 */
async function discardUnstartedDispatch(
  status: TLineDispatchStatus,
  scope: LineDispatchScope,
): Promise<boolean> {
  if (!isUnstartedDispatch(status)) return false;
  try {
    await cancelLineDispatch(status.dispatch.id, scope.organizationId);
    acknowledgeDispatchId(status.dispatch.id, scope.acknowledgedStorageKey);
    return true;
  } catch (error) {
    // 409/404はbackendが開始済みと判断した想定内の競合。それ以外は原因が追えるよう残す。
    // axiosはESMでjestが解釈できないため、import せずresponse.statusだけを読む。
    const statusCode =
      typeof error === 'object' && error !== null && 'response' in error
        ? (error as { response?: { status?: number } }).response?.status
        : undefined;
    if (statusCode !== 409 && statusCode !== 404) {
      // eslint-disable-next-line no-console -- 運用調査のため想定外だけ記録する
      console.warn(
        '[lineDispatch] 未着手dispatchの破棄に失敗',
        status.dispatch.id,
        statusCode,
      );
    }
    return false;
  }
}

async function retrySealedDispatch(
  status: TLineDispatchStatus,
  scope: LineDispatchScope,
): Promise<LineDispatchRecoveryDecision> {
  const recovered = await retryLineDispatch(
    status.dispatch.id,
    scope.organizationId,
  );
  const decision = terminalDecision(recovered, scope.acknowledgedStorageKey);
  if (decision) return decision;

  // UNKNOWN/RUNNINGのままなら同じoperationだけを未解決として保持する。
  // 新しいsource IDでproviderへ送ることは許可しない。
  throw new Error(sendAllMessageStrings.sendFailed);
}

/**
 * backend journalを正本に、producerを呼ぶ前の復旧方法を決める。
 * unsealed PLANNEDだけは本文を持つ同じproducer endpointへ戻し、
 * seal済みoperationは保存済みbodyだけを使うbodyless retryへ送る。
 */
export async function resolveLineDispatchRecovery(
  input: RecoverLineDispatchInput,
  // 複数の await を挟むため、操作開始時の組織と保存先を全呼び出しへ固定する
  scope: LineDispatchScope = UNSCOPED,
): Promise<LineDispatchRecoveryDecision> {
  const { organizationId, acknowledgedStorageKey } = scope;
  const exact = await getLineDispatchBySource(
    {
      sourceType: input.sourceType,
      sourceKey: input.sourceKey,
      sourceRevision: input.sourceRevision,
    },
    organizationId,
  );
  if (exact) {
    // 同じsource identityでも、intentが違うか開始者が自分でなければ、別のoperationが
    // 先にそのkeyを使い切っている。回収すると自分の本文を送らないまま
    // 「送信しました」と表示するので、進行中かどうかに関わらずkey使い切りとして
    // 伝える。呼び出し元がidentityを解放し、新しいsource keyの新規operationとして
    // 送り直せば既存operationには触れない。
    // 例: 別PCで開いたままの下書きを編集して送る／同じ下書きを別の管理者が先に送る。
    if (
      exact.dispatch.intentHash !== input.intentHash ||
      !exact.dispatch.startedByCurrentMember
    ) {
      throw new Error(sendAllMessageStrings.sendSourceAlreadyUsed);
    }
    const decision = terminalDecision(exact, acknowledgedStorageKey);
    if (decision) return decision;
    if (
      currentStatus(exact) === 'PLANNED' &&
      exact.dispatch.sealedAt === null
    ) {
      return 'RUN_PRODUCER';
    }
    return retrySealedDispatch(exact, scope);
  }

  // localStorageが消えた場合も、直近のbackend operationを先に照合する。
  // source keyを復元できないunsealed operationは安全に再planできないため停止する。
  const recent = (
    await getRecentLineDispatches(
      {
        sourceType: input.sourceType,
      },
      organizationId,
    )
  ).filter(
    (candidate) =>
      !isAcknowledgedDispatch(candidate.dispatch.id, acknowledgedStorageKey),
  );
  // recentは組織単位で他の管理者のoperationも含む。intentHashは本文と送信条件だけの
  // hashなので、同じ内容を送る別管理者の配信を自分のresponse loss回復と誤認し、
  // producerを呼ばないまま成功表示にしたり、その人の配信をcancel/retryしてしまう。
  // 回収・破棄の対象は自分が開始したoperationだけに限る。
  const matchingIntent = recent.find(
    (candidate) =>
      candidate.dispatch.intentHash === input.intentHash &&
      candidate.dispatch.startedByCurrentMember,
  );
  if (matchingIntent) {
    // 窓を過ぎた完了済みの同一intentは、response lossの取りこぼしではなく
    // 「同じ内容をもう一度送る」正当な再送とみなす。ここで回収すると
    // producerを呼ばないまま成功表示になり、送ったつもりの未送信が生まれる。
    // 別PC・別ブラウザではlocalStorageのacknowledgedが効かないため窓で切る。
    if (
      isTerminalDispatch(matchingIntent) &&
      !isWithinResponseLossWindow(matchingIntent)
    ) {
      acknowledgeDispatchId(matchingIntent.dispatch.id, acknowledgedStorageKey);
      return 'RUN_PRODUCER';
    }
    const decision = terminalDecision(matchingIntent, acknowledgedStorageKey);
    if (decision) return decision;
    // source keyを復元できない未着手operationは1件も送っていないため、
    // 破棄して同じintentを新しいoperationとして送り直す。
    if (await discardUnstartedDispatch(matchingIntent, scope)) {
      return 'RUN_PRODUCER';
    }
    // cancelを拒まれた未着手operationは、backendが開始済みと判断した合図で
    // 手元のrecentが古いだけ。同一intentの自分のoperationなのでbodyless retryで
    // 回収する。本当に未sealのままならbackendがretryを409で弾き、
    // 次の試行でcancelからやり直す。
    return retrySealedDispatch(matchingIntent, scope);
  }

  // intent不一致のactive operationが残っていても、新規送信は止めない
  // （進行中配信1件が組織全体の一斉送信を長時間塞いでいた設計の撤去）。
  // 自動破棄もしない。recentはorganization単位で他ユーザーのoperationも含み、
  // 計画中(planPartのページング中)のdispatchもseal前=未着手に見えるため、
  // 破棄すると他人の進行中配信をCANCELLEDにしてしまう。
  // 自分の再送と確認できるintent一致の場合だけ、上のmatchingIntent分岐で破棄する。
  return 'RUN_PRODUCER';
}

/** 正常なproducer応答で確認できたterminal dispatchをrecent回復対象から外す。 */
export async function acknowledgeLineDispatchProducerResult(
  input: Omit<RecoverLineDispatchInput, 'intentHash'>,
  scope: LineDispatchScope = UNSCOPED,
): Promise<void> {
  const exact = await getLineDispatchBySource(input, scope.organizationId);
  if (exact) {
    terminalDecision(exact, scope.acknowledgedStorageKey);
  }
}

export function useLineDispatchRecovery(): {
  recover: (
    input: RecoverLineDispatchInput,
  ) => Promise<LineDispatchRecoveryDecision>;
  acknowledgeProducerResult: (
    input: Omit<RecoverLineDispatchInput, 'intentHash'>,
  ) => Promise<void>;
  isRecovering: boolean;
} {
  // 組織スコープの画面は組織ごとに作り直されるため、render 時の組織が操作開始時の組織になる
  const organizationId = useOrganizationScopeOrNull()?.organizationId ?? null;
  const uid = useAuth().user?.uid;
  const scope = useMemo<LineDispatchScope>(
    () =>
      uid && organizationId !== null
        ? {
            organizationId,
            acknowledgedStorageKey: dispatchAcknowledgedStorageKey(
              uid,
              organizationId,
            ),
          }
        : { ...UNSCOPED, organizationId },
    [organizationId, uid],
  );
  const mutation = useMutation({
    mutationFn: (input: RecoverLineDispatchInput) =>
      resolveLineDispatchRecovery(input, scope),
  });
  const acknowledgeProducerResult = useCallback(
    (input: Omit<RecoverLineDispatchInput, 'intentHash'>) =>
      acknowledgeLineDispatchProducerResult(input, scope),
    [scope],
  );
  return {
    recover: mutation.mutateAsync,
    acknowledgeProducerResult,
    isRecovering: mutation.isPending,
  };
}
